// 事件頁的每小時分布（VMS UX 評審 2026-10-13 P1-3 ③、SPEC EVT-02 時間範圍選擇器）：
// 長條圖加時間刻度、滑過顯示該格時間與筆數，可在長條圖上拖選時段（= 設時間範圍，寫成網址 from / to）；點一格 = 那一個小時。
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatTime } from '../../lib/tz';

export interface HourBucket {
  /** 這一格的開頭（ISO） */
  value: string;
  count: number;
}

/** datetime-local 的值（本機時間到分鐘），跟自訂時間範圍的輸入框同一種格式。 */
export function localInputOf(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 拖選的兩格 → 時間範圍（含頭含尾：從較早那格的開頭到較晚那格的結束）。 */
export function rangeOfBuckets(buckets: HourBucket[], a: number, b: number): { from: string; to: string } | null {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (!buckets[lo] || !buckets[hi]) return null;
  const from = Date.parse(buckets[lo].value);
  const to = Date.parse(buckets[hi].value) + 3600_000;
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return { from: localInputOf(from), to: localInputOf(to) };
}

/**
 * 刻度放在哪幾格（第二輪 B2-13：刻度不重疊）：最多 want 個、每 step 格一個；跨日那一格（00:00）一定放，
 * 但它取代離它最近的一般刻度（不另外加），所以任兩個刻度至少隔 step / 2 格。
 */
export function tickIndexes(buckets: HourBucket[], want = 6): number[] {
  if (!buckets.length) return [];
  const step = Math.max(1, Math.ceil(buckets.length / Math.max(1, want)));
  const out = new Set<number>();
  for (let i = 0; i < buckets.length; i += step) out.add(i);
  buckets.forEach((b, i) => {
    if (i === 0 || new Date(Date.parse(b.value)).getHours() !== 0) return;
    for (const j of [...out]) if (j !== i && Math.abs(j - i) < step) out.delete(j);
    out.add(i);
  });
  return [...out].sort((x, y) => x - y);
}

/**
 * 0 筆的小時也畫一格（第七輪 B7-13：facet 只回有事件的小時，07、08、14 時直接消失、軸距不等，看起來像活動沒斷）：
 * 依時間排好，第一格到最後一格之間缺的小時補 count 0；跨度超過 maxHours 就不補（照原樣）。
 */
export function fillHours(buckets: HourBucket[], maxHours = 24 * 31): HourBucket[] {
  const list = buckets
    .map((b) => ({ b, at: Date.parse(b.value) }))
    .filter((x) => Number.isFinite(x.at))
    .sort((a, b) => a.at - b.at);
  if (list.length < 2) return list.map((x) => x.b);
  const first = list[0].at;
  const last = list[list.length - 1].at;
  if ((last - first) / 3600_000 > maxHours) return list.map((x) => x.b);
  const by = new Map(list.map((x) => [x.at, x.b]));
  const out: HourBucket[] = [];
  for (let at = first; at <= last; at += 3600_000) out.push(by.get(at) ?? { value: new Date(at).toISOString(), count: 0 });
  return out;
}

/** 長條圖寬度 → 刻度數（一個刻度約 56 px，至少 2 個、最多 8 個）。 */
export function ticksForWidth(px: number): number {
  return Math.max(2, Math.min(8, Math.floor(px / 56)));
}

export function HourHistogram({ buckets: raw, tz, onRange }: { buckets: HourBucket[]; tz: string; onRange(r: { from: string; to: string }): void }) {
  const buckets = fillHours(raw);
  const { t, i18n } = useTranslation();
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null);
  const max = Math.max(1, ...buckets.map((f) => f.count));
  // 刻度數量隨寬度遞減（B2-13）
  const wrapRef = useRef<HTMLDivElement>(null);
  const [want, setWant] = useState(6);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => setWant(ticksForWidth(el.clientWidth || 360));
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const ticks = new Set(tickIndexes(buckets, want));
  const sel = drag ? [Math.min(drag.a, drag.b), Math.max(drag.a, drag.b)] : null;
  const finish = () => {
    if (!drag) return;
    const r = rangeOfBuckets(buckets, drag.a, drag.b);
    setDrag(null);
    if (r) onRange(r);
  };
  // 刻度：時間一行；第一個刻度、以及跟前一個刻度不同天的刻度，日期另起一行（B2-13：不擠成「23:00/13」；
  // 第三輪：警報分頁跨好幾天時出現兩個「08:00」又沒有日期 → 換日就寫日期，用家戶時區判斷）
  const dayFmt = new Intl.DateTimeFormat(i18n.language, { month: 'numeric', day: 'numeric', timeZone: tz });
  const tickList = [...ticks].sort((x, y) => x - y);
  const dayOf = (i: number) => dayFmt.format(new Date(Date.parse(buckets[i].value)));
  const label = (b: HourBucket, i: number) => {
    const sec = Date.parse(b.value) / 1000;
    const k = tickList.indexOf(i);
    const date = k <= 0 || dayOf(tickList[k - 1]) !== dayOf(i) ? dayOf(i) : '';
    return (
      <>
        <span className="hour-tick-time">{formatTime(sec, tz, i18n.language, false)}</span>
        {date && <span className="hour-tick-date">{date}</span>}
      </>
    );
  };
  return (
    <div className="hour-hist-wrap" data-testid="hour-hist" ref={wrapRef}>
      <div className="hour-hist" role="group" aria-label={t('search.histogram')} onPointerLeave={() => setDrag(null)} onPointerUp={finish}>
        {buckets.map((f, i) => (
          <span
            key={f.value}
            className={'hour-bar' + (sel && i >= sel[0] && i <= sel[1] ? ' sel' : '')}
            data-hour={f.value}
            style={{ height: `${Math.max(4, (f.count / max) * 40)}px` }}
            title={t('search.hist_tip', { at: formatDateTime(Date.parse(f.value) / 1000, tz, i18n.language), n: f.count })}
            onPointerDown={(e) => {
              e.preventDefault();
              setDrag({ a: i, b: i });
            }}
            onPointerEnter={() => setDrag((d) => (d ? { ...d, b: i } : d))}
          />
        ))}
      </div>
      <div className="hour-ticks" aria-hidden>
        {buckets.map((f, i) => (
          <span key={f.value} className="hour-tick">
            {ticks.has(i) ? label(f, i) : ''}
          </span>
        ))}
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        {t('search.hist_hint')}
      </p>
    </div>
  );
}
